import { gql, useApolloClient } from '@apollo/client';
import { FieldSelect } from '@dixa/lib-shared-habla';
import { ParentRef } from 'lib-knowledge-management-common/models/types';

import CategoryDropdown, { Value } from './categoryDropdown';
import s from './categoryTree.module.scss';

type CategoryTreeInputProps = {
  value: ParentRef | undefined;
  onChange: (parentRef: ParentRef) => void;

  /** Prevent articles being added to the collection. */
  withCollectionOption: boolean;

  /**
   * Prevent a category from being moved into itself or one of it's children.
   * The current category will be disabled and it's children filtered out.
   */
  currentCategoryId: string | undefined;

  disabledCategoryIds?: Array<string>;

  collectionId: string | undefined;

  required?: boolean;

  /**
   * Defaults to "Parent Category", which is what the category modals are
   * choosing. Callers picking where an *article* goes should say "Category",
   * as the article editor's settings panel does.
   */
  label?: string;
  placeholder?: string;

  /**
   * Stop the tree being picked from. A caller writing the collection and the
   * category separately needs this while the collection is in flight, since
   * until it lands the tree still lists the old collection's categories.
   */
  disabled?: boolean;
};

/**
 * Component to Render a tree of categories relating to the currently selected
 * item
 */
const CategoryTreeInput = ({
  value,
  onChange,
  withCollectionOption,
  currentCategoryId,
  disabledCategoryIds = [],
  collectionId,
  required,
  label = 'Parent Category',
  placeholder,
  disabled,
}: CategoryTreeInputProps): JSX.Element => {
  const { cache } = useApolloClient();

  const _value: Value | undefined | null =
    value?.__typename === 'Category'
      ? cache.readFragment({
          id: `Category:${value?.id}`,
          fragment: gql`
            fragment CategoryTitles on Category {
              id
              titles {
                title
                languageId
              }
            }
          `,
        })
      : value;

  // Make sure we have the category with name, so the we can so the
  // title before loading the list of categories.

  if (!collectionId) {
    return (
      <FieldSelect
        disabled={true}
        searchable
        label={label}
        required={required}
        placeholder={placeholder ?? 'Select parent category'}
        input={{
          name: value?.id || 'Collection',
          value: value?.id || 'collection',
          onChange: () => {
            // Do nothing, input disabled
          },
        }}
      />
    );
  }

  return (
    <CategoryDropdown
      value={_value}
      onChange={onChange}
      collectionId={collectionId}
      className={s.categoryTreeInput}
      label={label}
      required={required}
      disabled={disabled}
      currentCategoryId={currentCategoryId}
      disabledCategoryIds={disabledCategoryIds}
      placeholder={
        placeholder ??
        (withCollectionOption ? 'Select a parent' : 'Select a parent category')
      }
      withCollectionOption={withCollectionOption}
    />
  );
};

export default CategoryTreeInput;
